// ============================================================
// useMediaQuery.js
// Subscribes to a CSS media query from JS.
//
// Used by AppShell to decide whether the navigation renders as the
// persistent desktop rail or as an off-canvas drawer. That decision
// cannot be CSS-only: the drawer is a native <dialog> driven by
// showModal(), which is an imperative call, so something in JS has
// to know which side of the breakpoint we are on.
//
// Rendering one or the other (rather than both, with one hidden)
// keeps the nav in the DOM exactly once — no duplicate landmarks, no
// duplicate NavLinks for a screen reader to walk, and no second badge
// fetch.
//
// SSR/jsdom-safe: matchMedia is absent in some test environments, so
// an unavailable API resolves to `false` — the desktop rail — which
// is the layout every existing test already expects.
// ============================================================

import { useEffect, useState } from 'react';

export default function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // Re-sync on mount as well as on change: the query may have moved
    // between the initial useState and this effect running.
    setMatches(mql.matches);
    // addEventListener is the modern API; addListener is the Safari <14
    // fallback and is what several jsdom matchMedia mocks implement.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, [query]);

  return matches;
}
